/* ⚠ Text's markup rules with no React and no "use client". Text imports
   Tooltip (the `<tooltip>` tag draws it), so Tooltip must never import Text or
   anything that imports Text: that is a cycle, and it would pull the HTML
   parser into the client bundle. Text's sanitizing lives in text.sanitize and
   text.tooltip, which only Text imports. */

import type { TextVariant } from "./text";

/**
 * Markup is a tag with its matching close, or a void element. `<[a-z][^>]*>`
 * alone is not enough: prose says "press <Enter> to continue" and "compare <p>
 * with <div>", and the parser turns those into elements. A close tag is what
 * separates authored markup from an angle bracket in a sentence.
 */
const PAIRED = /<([a-z][a-z0-9-]*)\b[^>]*>[\s\S]*?<\/\1\s*>/i;

/* Every void element, in two groups. `br`, `hr` and `wbr` mean something with
   no attributes at all, so bare is enough. The rest are also ordinary words —
   "set the <source> to HDMI 1", "paste the <link>" — so bare is prose and an
   attribute is what marks the real thing: `<source srcset=…>`. */
const VOID_BARE = /<(?:br|hr|wbr)\b[^>]*>/i;
const VOID_ATTR =
  /<(?:area|base|col|embed|img|input|link|meta|source|track)\s+[a-z-]+\s*=/i;
const SELF_CLOSING = /<[a-z][a-z0-9-]*\b(?:"[^"]*"|'[^']*'|[^>"'])*\/>/i;

/**
 * Phrasing content: the elements a <p> may legally contain.
 *
 * The test is INVERTED — anything not on this list needs a block host — because
 * phrasing content is a closed set in the spec while block content is
 * open-ended. An unknown or custom element is treated as block, which is the
 * safe direction: a div can host anything. `tooltip` is listed because Text
 * draws it as the Tooltip, whose root is an inline <span>.
 */
export const PHRASING: ReadonlySet<string> = new Set([
  "a", "abbr", "b", "bdi", "bdo", "br", "button", "cite", "code", "data",
  "datalist", "del", "dfn", "em", "i", "img", "input", "ins", "kbd", "label",
  "map", "mark", "meter", "noscript", "output", "picture", "progress", "q",
  "ruby", "rp", "rt", "s", "samp", "select", "slot", "small", "span", "strong",
  "sub", "sup", "template", "textarea", "time", "tooltip", "u", "var", "wbr"
]);

/**
 * Whether a string carries authored markup rather than an angle bracket in
 * prose. Exported because anything DERIVED from the same string — an accessible
 * name, a reporting key — has to agree with what renders, and a second opinion
 * about what counts as a tag is a second thing to get wrong.
 */
export function hasMarkup(value: string): boolean {
  return (
    PAIRED.test(value) ||
    VOID_BARE.test(value) ||
    VOID_ATTR.test(value) ||
    SELF_CLOSING.test(value)
  );
}

/* A self-closing tooltip, `<tooltip aria-label="x"/>`. HTML has no self-closing
   custom elements, so the parser reads it as an OPEN tag and swallows the rest
   of the sentence into the pop-up. Made an empty pair first, which draws no
   icon and leaves the sentence alone. Quote-aware, so a `>` or `/>` inside an
   attribute value is not the end of the tag; `(?![w-])` keeps `<tooltip-x>`
   and `<tooltipper>` out. */
const SELF_CLOSING_TOOLTIP = /<tooltip(?![\w-])((?:"[^"]*"|'[^']*'|[^>"'])*?)\s*\/>/gi;

export function normalizeTooltipTags(html: string): string {
  return html.replace(SELF_CLOSING_TOOLTIP, "<tooltip$1></tooltip>");
}

const TAG_NAME = /<([a-z][a-z0-9-]*)\b/gi;

/* A tooltip's body is flattened to inline before it reaches the panel, so a
   block tag inside one must not push the whole copy off its <p>. Depth is
   counted, so a nested tooltip's close does not end the outer body early; a tag
   that never closes is left alone, which can only choose a div, the safe side. */
const TOOLTIP_TOKEN = /<(\/?)tooltip(?![\w-])(?:"[^"]*"|'[^']*'|[^>"'])*>/gi;

function withoutTooltipBodies(html: string): string {
  TOOLTIP_TOKEN.lastIndex = 0;
  let kept = "";
  let resume = 0;
  let opened = 0;
  let depth = 0;
  let token = TOOLTIP_TOKEN.exec(html);
  while (token !== null) {
    if (token[1] === "") {
      if (depth === 0) {
        kept += html.slice(resume, token.index);
        opened = token.index;
      }
      depth += 1;
    } else if (depth > 0) {
      depth -= 1;
      if (depth === 0) {
        resume = token.index + token[0].length;
      }
    }
    token = TOOLTIP_TOKEN.exec(html);
  }
  return kept + html.slice(depth > 0 ? opened : resume);
}

/** Whether markup holds a block element, so Text will draw it in a div. Exported
 *  so a parent can move its own wrapper off a <p> before that happens. */
export function needsBlockHost(html: string): boolean {
  const outside = withoutTooltipBodies(normalizeTooltipTags(html));
  TAG_NAME.lastIndex = 0;
  let match = TAG_NAME.exec(outside);
  while (match !== null) {
    if (!PHRASING.has(match[1].toLowerCase())) {
      return true;
    }
    match = TAG_NAME.exec(outside);
  }
  return false;
}

/* The CMS membership sets. Here rather than in ./text for the reason at the top
   of this file: a server-side adapter needs only these tables, and importing
   them from ./text drags the HTML parser in with them. The variant union stays
   in ./text (sibling specs read it there) and is imported type-only. */

/** What a heading-type CMS field (`*_level`) may render as. */
export type TextLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

export const TEXT_LEVELS: Record<TextLevel, true> = {
  h1: true,
  h2: true,
  h3: true,
  h4: true,
  h5: true,
  h6: true,
  p: true
};

/** What any other text CMS field (`*_element`) may render as. */
export type TextBodyElement = "p" | "span" | "div";

export const TEXT_BODY_ELEMENTS: Record<TextBodyElement, true> = {
  p: true,
  span: true,
  div: true
};

/** Variants a heading-type field (`*_level`) may take. The body steps are in
 *  because a heading drawn at reading size is a published design — a card
 *  title, a FAQ question. `label`, `eyebrow` and the number and text-link
 *  steps stay out: they are too small to read as a heading at all, and
 *  `*_level` already carries what assistive technology announces. */
export type TextHeadingVariant = Extract<
  TextVariant,
  | "headline-1"
  | "headline-2"
  | "headline-3"
  | "headline-1-cmp"
  | "headline-2-cmp"
  | "headline-3-cmp"
  | "subhead-1"
  | "subhead-1-cmp"
  | "subhead-2"
  | "body-1"
  | "body-2"
  | "body-3"
>;

/** Variants any other text field (`*_element`) may take. */
export type TextBodyVariant = Extract<
  TextVariant,
  "body-1" | "body-2" | "body-3" | "subhead-1" | "subhead-2" | "label" | "eyebrow"
>;

export const TEXT_HEADING_VARIANTS: Record<TextHeadingVariant, true> = {
  "headline-1": true,
  "headline-2": true,
  "headline-3": true,
  "headline-1-cmp": true,
  "headline-2-cmp": true,
  "headline-3-cmp": true,
  "subhead-1": true,
  "subhead-1-cmp": true,
  "subhead-2": true,
  "body-1": true,
  "body-2": true,
  "body-3": true
};

/* Variants any other text field (`*_element`) may take. Number and
   text-link variants are never CMS-selectable. Not JSDoc: Storybook's docgen
   reads a documented export as a component and adds keys to the object. */
export const TEXT_BODY_VARIANTS: Record<TextBodyVariant, true> = {
  "body-1": true,
  "body-2": true,
  "body-3": true,
  "subhead-1": true,
  "subhead-2": true,
  label: true,
  eyebrow: true
};
